import React from "react";
import AnimatedBox from "app/components/common/AnimatedBox";
import { StyleSheet } from "react-native";
import ProfilePicture from "./ProfilePicture";
import ProfileStat from "./ProfileStat";
import i18n from "@microdoing/shared/i18n";
import { PICTURE_SIZE } from "./ProfilePicture";
import CompleteProfile from "./CompleteProfile";
import {
  Box,
  MainButton,
  MainStrokeButton,
  MainText,
  MainTitle,
} from "../../components/common";
import { useSelector } from "react-redux";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { pluralize, pluralizeI18n } from "app/utils/plurialize";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { useNavigation } from "@react-navigation/native";
import theme from "app/Theme/Theme";

const styles = StyleSheet.create({
  main: {
    marginTop: isWebDesktop ? 0 : theme.spacing.sm + PICTURE_SIZE / 3,
  },
});

interface IProps {
  picture?: string;
  name?: string;
  jobDescription?: string;
  actions?: number;
  streak?: number;
  pulseSent?: number;
  mbti?: string;
  bio?: string;
  disc?: string;
  progress?: number;
  isUserProfile?: boolean;
  externalUserId?: string;
  isPulseAnimated?: boolean;
  onChatOpen: () => void;
  sendPulseNotification: () => void;
}

const ProfileHeader = ({
  picture = "",
  name = "",
  jobDescription = "",
  actions = 0,
  streak = 0,
  pulseSent = 0,
  mbti = "",
  bio = "",
  disc = "",
  progress = 0,
  isUserProfile = true,
  onChatOpen,
  sendPulseNotification,
  isPulseAnimated = false,
  externalUserId = "",
}: IProps) => {
  const userId = useSelector(getUserId);

  const navigation = useNavigation();

  const navigateToSettings = () => navigation.navigate("UserProfileSettings");
  const percentProfileCompleted = (progress * 100).toFixed(0);
  return (
    <Box
      style={styles.main}
      marginBottom="sm"
      width="100%"
      paddingHorizontal="sm"
      paddingVertical="m"
      borderRadius={10}
      backgroundColor="neutral.white"
    >
      <Box alignItems="center" flexDirection="column">
        <ProfilePicture
          picture={picture}
          isUserProfile={isUserProfile}
          firstName={name}
        />
        <Box
          width="100%"
          marginTop="sm"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <MainTitle variant="l7-bold">{name}</MainTitle>
          {!!bio && (
            <MainText variant="l3-medium" color="neutral.900" marginTop="xs">
              {bio}
            </MainText>
          )}
          <Box
            width="100%"
            marginTop="sm"
            alignItems="center"
            justifyContent="space-evenly"
            flexDirection="row"
            borderRadius={6}
            paddingVertical="s"
            marginHorizontal="m"
          >
            <ProfileStat stat={actions} legend={pluralize("Action", actions)} />
            <ProfileStat
              stat={streak}
              legend={pluralizeI18n("streak", streak)}
            />
            <ProfileStat
              stat={pulseSent}
              legend={pluralize("Pulse", pulseSent)}
            />
          </Box>
        </Box>
      </Box>
      {userId !== externalUserId && (
        <>
          {!!isUserProfile && (
            <>
              <MainButton
                onPress={navigateToSettings}
                text={
                  percentProfileCompleted !== "100"
                    ? i18n.t("complete-profile-button")
                    : i18n.t("modify-my-profile")
                }
              />
              {progress < 1 && (
                <CompleteProfile
                  progress={progress}
                  onPress={navigateToSettings}
                />
              )}
            </>
          )}
          {!isUserProfile && (
            <Box
              width="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="row"
            >
              <Box flex={2} marginRight="s">
                <MainButton
                  onPress={onChatOpen}
                  text={i18n.t("send-message")}
                  textVariant="l3-semibold"
                />
              </Box>
              <Box flex={1}>
                <MainStrokeButton
                  text={i18n.t("pulse")}
                  textColor="brand.500"
                  onPress={sendPulseNotification}
                  isLoading={isPulseAnimated}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProfileHeader;
